import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import {
  EMAIL_VARIABLE_CATALOG,
  type VariableDefinition,
  type VariableKind,
} from '@/libs/correspondence';

interface VariableMenuProps {
  /** Called with the chosen token. Caller is responsible for actually
   * inserting the variable (chip in the RTE, literal text in plain inputs). */
  onSelect: (token: string) => void;
  onClose: () => void;
  /** Trigger element — used to detect outside-clicks and place the popover. */
  anchorRef: RefObject<HTMLElement | null>;
}

const KIND_LABEL: Record<VariableKind, string> = {
  computed: 'Berechnet',
  recipient: 'Empfänger',
  sender: 'Absender',
  static: 'Sonstiges',
};

/**
 * Categorises the catalog so the menu groups variables under "Berechnet" /
 * "Empfänger" / "Absender" / "Sonstiges" headings. Computed tokens
 * (`{{anrede}}`, `{{grussformel}}`) come first — they are by far the most
 * common insertion in a greeting/sign-off email.
 */
function groupByKind(
  catalog: readonly VariableDefinition[],
): { kind: VariableKind; items: VariableDefinition[] }[] {
  const order: VariableKind[] = ['computed', 'recipient', 'sender', 'static'];
  const groups: Record<VariableKind, VariableDefinition[]> = {
    computed: [],
    recipient: [],
    sender: [],
    static: [],
  };
  for (const def of catalog) groups[def.kind].push(def);
  return order
    .filter((k) => groups[k].length > 0)
    .map((kind) => ({ kind, items: groups[kind] }));
}

/** Estimated max menu height — used to flip above the anchor when there
 * isn't enough room below. Slightly larger than the CSS `max-height` of
 * the menu so the heuristic stays conservative. */
const ESTIMATED_MENU_HEIGHT = 380;
/** Approximate menu width — matches the CSS `min-width`/`max-width` band. */
const ESTIMATED_MENU_WIDTH = 320;

/** Resolves the design-system scope element the menu portals into. Falls
 * back to `document.body` only outside a DS-scoped tree (never expected). */
function getPortalRoot(): HTMLElement {
  if (typeof document === 'undefined') {
    // SSR guard — VariableMenu is client-only so this branch is unreachable
    // in practice, but it keeps the type narrowing honest.
    return null as unknown as HTMLElement;
  }
  return (
    (document.querySelector('[data-ds="eos"]') as HTMLElement | null) ??
    document.body
  );
}

/**
 * Popover menu that lists every variable, grouped by kind. Closes on
 * outside click or Escape. Used both by the rich-text editor toolbar and
 * by the inline button next to plain-text inputs (`VariableInsertButton`).
 *
 * Portals into the `[data-ds="eos"]` design-system scope so it escapes
 * every overflow ancestor (the rich-text editor's textbox, the scrollable
 * block list, etc.) and uses `position: fixed` placed against the
 * anchor's bounding rect — same pattern as `DsModal`. The position is
 * recomputed on viewport scroll/resize so the menu stays glued to its
 * trigger.
 */
export function VariableMenu({ onSelect, onClose, anchorRef }: VariableMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(
    null,
  );

  // Outside-click + Esc dismissal. Anchor's own click is ignored — it
  // toggles the menu itself.
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [anchorRef, onClose]);

  // Position is computed against the anchor's viewport rect. `useLayoutEffect`
  // so the first paint already shows the menu at the right spot — `useEffect`
  // would let the browser paint at (0,0) for one frame.
  useLayoutEffect(() => {
    const reposition = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) {
        setPosition(null);
        return;
      }
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;
      const spaceBelow = viewportH - rect.bottom;
      // Flip above the anchor when there isn't enough room below — common
      // when the menu opens from a button near the bottom of the viewport
      // (e.g. the last block in a long draft).
      const placeAbove =
        spaceBelow < ESTIMATED_MENU_HEIGHT && rect.top > spaceBelow;
      const top = placeAbove
        ? Math.max(8, rect.top - ESTIMATED_MENU_HEIGHT - 4)
        : Math.min(rect.bottom + 4, viewportH - 8);
      // Clamp horizontally so the menu doesn't run off the right edge.
      const left = Math.min(
        Math.max(8, rect.left),
        Math.max(8, viewportW - ESTIMATED_MENU_WIDTH - 8),
      );
      setPosition({ top, left });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [anchorRef]);

  const groups = groupByKind(EMAIL_VARIABLE_CATALOG);
  const root = getPortalRoot();
  if (!root) return null;

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      className="eb-var-menu is-portaled"
      style={
        position
          ? { top: position.top, left: position.left }
          : { visibility: 'hidden' }
      }
      // Stop pointer-down from blurring the underlying editor — important
      // for the RTE case so the chain().focus().insertVariable() command
      // lands at the right position.
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="eb-var-menu-header">Variable einfügen</div>
      {groups.map(({ kind, items }) => (
        <div key={kind} className="eb-var-menu-group">
          <div className="eb-var-menu-group-label">{KIND_LABEL[kind]}</div>
          <ul className="eb-var-menu-list" role="none">
            {items.map((def) => (
              <li key={def.token} role="none">
                <button
                  type="button"
                  role="menuitem"
                  className="eb-var-menu-item"
                  onClick={() => onSelect(def.token)}
                >
                  <span className="eb-var-menu-item-label">{def.label}</span>
                  <span className="eb-var-menu-item-token">
                    {'{{'}
                    {def.token}
                    {'}}'}
                  </span>
                  <span className="eb-var-menu-item-desc">{def.description}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>,
    root,
  );
}
